import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useDeleteProject } from '../api/queries'
import type { Project, ProjectDeleteSummary } from '../api/types'
import { Card } from './ui'

function count(value: number, one: string, many: string) {
  return value === 1 ? `1 ${one}` : `${value} ${many}`
}

/**
 * Slet sagen helt - kun for Superadmin (beslutning 076).
 *
 * Tænkt til testsager og sager, der er oprettet ved en fejl. En rigtig sag arkiveres i
 * stedet, så historikken bliver. Før sletningen hentes, hvad der forsvinder med sagen,
 * så bekræftelsen siger det ligeud - "12 opgaver og 3 dage i kalenderen" og ikke bare
 * "er du sikker?".
 */
export function DeleteProjectCard({ project }: { project: Project }) {
  const remove = useDeleteProject()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run() {
    setError(null)
    setBusy(true)
    try {
      const summary = await api.get<ProjectDeleteSummary>(`/api/projects/${project.id}/delete-summary`)

      const gone = [
        summary.tasks && count(summary.tasks, 'opgave', 'opgaver'),
        summary.comments && count(summary.comments, 'kommentar', 'kommentarer'),
        summary.calendarEntries && count(summary.calendarEntries, 'post i kalenderen', 'poster i kalenderen'),
        summary.defects && count(summary.defects, 'mangel', 'mangler'),
        summary.files && `${count(summary.files, 'fil', 'filer')} på listen (selve filerne bliver liggende i SharePoint)`
      ].filter(Boolean)

      const kept = [
        summary.devices && count(summary.devices, 'enhed', 'enheder'),
        summary.stockMovements && count(summary.stockMovements, 'lagerbevægelse', 'lagerbevægelser'),
        summary.supportTickets && count(summary.supportTickets, 'henvendelse', 'henvendelser'),
        summary.warranties && count(summary.warranties, 'garanti', 'garantier')
      ].filter(Boolean)

      const message =
        `Slet sagen "${project.name}" permanent?\n\n` +
        (gone.length ? `Slettes med sagen: ${gone.join(', ')}.\n` : 'Der er intet andet på sagen.\n') +
        (kept.length ? `Bliver, men mister koblingen til sagen: ${kept.join(', ')}.\n` : '') +
        '\nDet kan ikke fortrydes. Er det en rigtig sag, så arkivér den i stedet med knappen Arkivér øverst på sagen.'

      if (!window.confirm(message)) return

      await remove.mutateAsync(project.id)
      navigate('/projects')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sagen kunne ikke slettes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Slet sagen">
      <p className="small muted" style={{ marginTop: 0 }}>
        Kun for Superadmin - til testsager og sager oprettet ved en fejl. En rigtig sag skal arkiveres, så
        historikken bliver.
      </p>
      {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
      <button className="btn btn--danger btn--sm" disabled={busy} onClick={() => void run()}>
        {busy ? 'Sletter ...' : 'Slet sagen permanent'}
      </button>
    </Card>
  )
}
